import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import {Compbaser} from "../../../components/compbaser";
import {StoreSelectorsService} from "../../../services";
import {EnumConnectionType} from "../../../interfaces";
import {HOME_OPEN_CREATE_SCENE_KEY} from "../home.component";

/** Glass onboarding card. Shows three live-ticking steps:
 *   1) create your first scene  → scenesTotal > 0
 *   2) connect a player         → stationsTotal > 0
 *   3) go live on a screen      → stationsOnline > 0
 *  The whole card auto-hides once all three are satisfied so returning
 *  power users never see it again. State is derived from the same store
 *  observables the hero KPI strip already subscribes to — no extra
 *  API traffic. */
@Component({
    selector: "app-home-onboarding",
    templateUrl: "./home-onboarding.component.html",
    styleUrls: ["./home-onboarding.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HomeOnboardingComponent extends Compbaser implements OnInit {
    /** Step completion flags. Updated reactively from the store. The
     *  card itself never self-hides — visibility is owned by the
     *  enclosing app-home-section wrapper (minimize toggle), so it
     *  behaves consistently with every other home section.
     *
     *  hasDownload is derived from hasPlayer: if there's a connected
     *  player on the account, the user obviously downloaded the player
     *  app at some point. There's no separate server-side flag for
     *  "downloaded" so the proxy keeps the step honest without adding
     *  a tracking call. */
    hasScene = false;
    hasDownload = false;
    hasPlayer = false;
    hasOnline = false;

    constructor(
        private router: Router,
        private storeSelector: StoreSelectorsService,
        private cdr: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.cod(
            this.storeSelector.getScenes().subscribe((scenes) => {
                this.hasScene = (scenes?.length || 0) > 0;
                this.cdr.markForCheck();
            }),
        );
        this.cod(
            this.storeSelector.getStationsComplete(EnumConnectionType.ALL).subscribe((stations) => {
                const list = stations || [];
                this.hasPlayer = list.length > 0;
                this.hasDownload = this.hasPlayer; // any connected player implies the app was downloaded
                this.hasOnline = list.some((s) => (s as any)?.stat?.$?.connection === "1");
                this.cdr.markForCheck();
            }),
        );
    }

    /** Total step count — single source of truth for both the count
     *  chip and the progress bar. Bump if a new step is added. */
    readonly totalSteps = 4;

    /** Completed step count drives the progress bar (0–totalSteps). */
    get completedCount(): number {
        return (this.hasScene ? 1 : 0)
            + (this.hasDownload ? 1 : 0)
            + (this.hasPlayer ? 1 : 0)
            + (this.hasOnline ? 1 : 0);
    }

    /** 0–100 for the css width on the progress fill. */
    get progressPct(): number {
        return Math.round((this.completedCount / this.totalSteps) * 100);
    }

    /** Step 1 click — same path the hero CTA uses (open pick-scene-type
     *  dialog on the scene list page). */
    onStepScene(): void {
        if (this.hasScene) {
            this.router.navigateByUrl("/editor/list");
            return;
        }
        try {
            window.localStorage.setItem(HOME_OPEN_CREATE_SCENE_KEY, "1");
        } catch { /* quota — ignore */ }
        this.router.navigateByUrl("/editor/list");
    }

    /** Step 2 — download the player app. Routes to the same /download
     *  page the nav's "download" item points at. */
    onStepDownload(): void {
        this.router.navigateByUrl("/download");
    }

    onStepPlayer(): void {
        this.router.navigateByUrl("/stations");
    }

    onStepOnline(): void {
        this.router.navigateByUrl("/stations");
    }
}
